/**
 * Panel positions for the three stacked views (EN-10545).
 *
 * THE DESIGN RULE — every view change is a double-buffered CUT.
 *
 * This started as travel slides (entering view gliding in on its own axis)
 * with a cut only for table↔grid. The slides died on 8/21: the LOBBY panel is
 * unmounted while inactive (its decorative GSAP loops must not run invisibly
 * under a live table), so a travel slide animated the leaving view away over
 * the bare BACKDROP while the lobby was still mounting — a second of flat
 * brown after tapping "+" to add a table (Jason 8/21, with screenshot). The
 * cut already solved exactly this class of problem for table↔grid, and the
 * design owner's call generalizes it: instant transitions read as higher
 * quality and better performance than watching a page translate.
 *
 * The cut: while a swap is STAGED, the LEAVING panel keeps painting at the
 * origin on top — still live, fully opaque — while the entering view mounts
 * beneath it. SlideContainer flips the z-order two animation frames after the
 * view change, once the entering panel has actually painted. Nothing
 * animates, nothing is ever translucent, and the backdrop can never show:
 * the player sees one finished view, then the other.
 *
 * After the flip the hidden panel RE-PARKS at its off-screen rest, instantly
 * and invisibly. Leaving it at the origin was a real bug once already: an
 * edge drag past the first/last table rubber-bands the carousel and exposed
 * the parked GRID sitting behind the felt (Jason 8/21). The lobby panel is
 * the exception — it is pinned at the origin and never transformed, because
 * it unmounts instead of parking (see SlideContainer's render gate).
 *
 * Everything here is % translate3d, matching the pinned-lobby reasoning in
 * SlideContainer: panels cover each other by z-order, never by motion.
 */

export type PanelView = 'lobby' | 'table' | 'grid';

const AT_ORIGIN = 'translate3d(0, 0, 0)';
const BELOW = 'translate3d(0, 100%, 0)';
const OFF_LEFT = 'translate3d(-100%, 0, 0)';

/** No pair animates — the cut is a z-flip owned by SlideContainer. */
export const NO_TRANSITION = 'none';

export type PanelMotion = {
  tableTransform: string;
  gridTransform: string;
  tableOpacity: number;
  gridOpacity: number;
  tableTransition: string;
  gridTransition: string;
};

export const resolvePanelMotion = (
  view: PanelView,
  /** The view we came from — null on first mount (rest positions apply). */
  prev: PanelView | null,
  /**
   * True while a cut is staged: the leaving view still paints on top while
   * the entering one mounts beneath, so BOTH hold the origin. Once the flip
   * lands (staged=false), the active view keeps the origin and the hidden
   * panel re-parks off-screen.
   */
  staged = false
): PanelMotion => ({
  tableTransform: view === 'table' || (staged && prev === 'table') ? AT_ORIGIN : BELOW,
  gridTransform: view === 'grid' || (staged && prev === 'grid') ? AT_ORIGIN : OFF_LEFT,
  // FULL COVERAGE on every frame — the anti-flash invariant. Any transparency
  // window lets a half-mounted skeleton (or the backdrop) show through.
  tableOpacity: 1,
  gridOpacity: 1,
  tableTransition: NO_TRANSITION,
  gridTransition: NO_TRANSITION
});
